import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Package } from 'lucide-react';
import { formatPrice } from '@/lib/utils';

export interface QuantityTier {
  minQuantity: string;
  maxQuantity: string;
  price: string;
}

interface QuantityPricingDisplayProps {
  tiers: QuantityTier[];
  basePrice: number;
  selectedQuantity: number;
}

export function QuantityPricingCarousel({
  tiers,
  basePrice,
  selectedQuantity,
}: QuantityPricingDisplayProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const sortedTiers = [...tiers].sort((a, b) => parseInt(a.minQuantity) - parseInt(b.minQuantity));

  useEffect(() => {
    const matchIndex = sortedTiers.findIndex(
      (tier) =>
        selectedQuantity >= parseInt(tier.minQuantity) &&
        selectedQuantity <= parseInt(tier.maxQuantity)
    );
    if (matchIndex !== -1) {
      setActiveIndex(matchIndex);
    } else if (selectedQuantity > 0) {
      const lastTier = sortedTiers[sortedTiers.length - 1];
      if (selectedQuantity >= parseInt(lastTier.minQuantity)) {
        setActiveIndex(sortedTiers.length - 1);
      }
    }
  }, [selectedQuantity, sortedTiers]);

  return (
    <div className="w-full">
      {/* Title */}
      <div className="flex items-center gap-1.5 mb-2">
        <Package className="w-3.5 h-3.5 text-brand-600" />
        <span className="text-[11px] font-bold text-gray-800">Wholesale Pricing</span>
      </div>

      {/* Cards - wraps to new line */}
      <div className="flex flex-wrap gap-1.5">
        {sortedTiers.map((tier, index) => {
          const tierPrice = parseFloat(tier.price);
          const tierSavings = basePrice - tierPrice;
          const tierSavingsPercent = tierSavings > 0 ? Math.round((tierSavings / basePrice) * 100) : 0;
          const isActive = index === activeIndex;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.04, duration: 0.2 }}
              className={`inline-flex flex-col rounded-md py-1.5 px-2.5 transition-all duration-200 whitespace-nowrap ${
                isActive
                  ? 'bg-gradient-to-br from-brand-500 to-brand-600 text-white ring-1 ring-brand-300'
                  : 'bg-gray-50 text-gray-700 border border-gray-200'
              }`}
            >
              {/* Row 1: Quantity + Discount - never wraps */}
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span className={`text-[10px] font-semibold leading-none ${isActive ? 'text-white' : 'text-gray-600'}`}>
                  {tier.minQuantity}-{tier.maxQuantity} pcs
                </span>
                {tierSavings > 0 && (
                  <span className={`inline-flex items-center gap-0.5 text-[8px] font-bold leading-none ${
                    isActive ? 'text-brand-100' : 'text-brand-600'
                  }`}>
                    <TrendingDown className="w-2 h-2" />
                    {tierSavingsPercent}%
                  </span>
                )}
              </div>

              {/* Row 2: Price - single line */}
              <span className={`text-xs font-bold mt-0.5 leading-none whitespace-nowrap ${isActive ? 'text-white' : 'text-gray-900'}`}>
                {formatPrice(tierPrice)}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
